/**
 * The execute step of the map-click pipeline: the single place that turns a `ClickResolution`
 * into side effects (selection state, overlapping-feature stack, router navigation, drawer
 * minimize/restore). It takes no interaction decision of its own - `resolveClick` already did.
 */
import { useMediaQuery, useTheme } from '@mui/material';
import { usePoisState } from 'state/usePoisState';
import { useMapDrawerEntityForPoiState } from 'state/useMapDrawerEntityForPoiState';
import {
  resolveOverlappingFeaturesUpdate,
  useOverlappingFeaturesState,
} from 'state/useOverlappingFeaturesState';
import { useClearMapSelectionsForRouterEntity } from 'features/mapping/hooks/useClearMapSelectionsForRouterEntity';
import { useActivateOverlappingFeature } from 'features/mapping/hooks/useActivateOverlappingFeature';
import { useMapEntityDrawer } from 'features/mapping/hooks/useMapEntityDrawer';
import {
  ClickCandidate,
  ClickResolution,
} from 'features/mapping/mapClick/resolution';
import { OverlappingFeatureAction } from 'features/mapping/mapping.types';
import { ClickTarget, ClickTargets } from './candidates';

type ExecuteClickResolutionContext = {
  targets: ClickTargets;
};

type ExecuteClickResolution = (
  resolution: ClickResolution,
  ctx: ExecuteClickResolutionContext,
) => void;

const targetOf = (
  candidate: ClickCandidate,
  targets: ClickTargets,
): ClickTarget => {
  const target = targets.get(candidate);
  if (!target) {
    throw new Error('Missing click target for a resolved candidate');
  }
  return target;
};

/** Navigational targets are terminal, so they never reach the overlapping stack. */
const actionOf = (
  candidate: ClickCandidate,
  targets: ClickTargets,
): OverlappingFeatureAction => {
  const target = targetOf(candidate, targets);
  if (target.category === 'navigational') {
    throw new Error('Navigational target has no overlapping-feature action');
  }
  return target.action;
};

export const useExecuteClickResolution = (): ExecuteClickResolution => {
  const { setSelectedPois, clearSelectedPois } = usePoisState();
  const { setMapDrawerEntityForPoi } = useMapDrawerEntityForPoiState();
  const { minimizeEntityDrawer, restoreEntityDrawer } = useMapEntityDrawer();
  const {
    features: overlappingFeatures,
    activeIndex: overlappingActiveIndex,
    setOverlappingFeatures,
    clearOverlappingFeatures,
  } = useOverlappingFeaturesState();
  const clearMapSelectionsForRouterEntity =
    useClearMapSelectionsForRouterEntity();
  const { activateFeature } = useActivateOverlappingFeature();
  const theme = useTheme();
  // Tapping a stacked layer again cycles to the next one on mobile, where the
  // Next/Prev footer is harder to reach than the map itself.
  const isMobile = useMediaQuery(theme.breakpoints.down('tablet'));

  const applyOverlappingFeatures = (
    actions: OverlappingFeatureAction[],
  ): void => {
    const nextStack = resolveOverlappingFeaturesUpdate({
      previous: {
        features: overlappingFeatures,
        activeIndex: overlappingActiveIndex,
      },
      nextFeatures: actions,
      allowCycling: isMobile,
    });

    setOverlappingFeatures(nextStack);
    activateFeature(nextStack.features[nextStack.activeIndex]);
  };

  return (resolution, { targets }) => {
    switch (resolution.mode) {
      case 'navigate': {
        const target = targetOf(resolution.target, targets);
        if (target.category === 'navigational') {
          // photoMarker / report: clear selections and fire the layer handler (navigates away).
          clearSelectedPois();
          clearOverlappingFeatures();
          target.run();
        } else if (target.category === 'router') {
          clearMapSelectionsForRouterEntity();
          target.navigate();
          restoreEntityDrawer();
        } else {
          // A lone externalGeoEvent activates its card route directly.
          clearOverlappingFeatures();
          activateFeature(target.action);
        }
        break;
      }

      case 'overlap': {
        applyOverlappingFeatures(
          resolution.ordered.map((candidate) => actionOf(candidate, targets)),
        );
        break;
      }

      case 'select': {
        clearOverlappingFeatures();
        const dialogPois = resolution.dialog
          .map((candidate) => targetOf(candidate, targets))
          .flatMap((t) => (t.category === 'poi' ? [t.poi] : []));
        const drawerEntities = resolution.drawer
          .map((candidate) => targetOf(candidate, targets))
          .flatMap((t) => (t.category === 'drawerEntity' ? [t.entity] : []));

        if (dialogPois.length) {
          setSelectedPois(dialogPois);
          minimizeEntityDrawer();
        }
        if (drawerEntities.length) {
          setMapDrawerEntityForPoi(drawerEntities);
        } else if (resolution.clearDeferredDrawer) {
          // A polygon-only drawer entity was deferred in a mixed click - clear any stale one.
          setMapDrawerEntityForPoi(null);
        }
        break;
      }

      case 'empty':
      default:
        clearOverlappingFeatures();
        break;
    }
  };
};
